import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Badge, Button, Form, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import LoadingOverlay from "app/orderTool/components/LoadingOverlay";
import HelpTip from "app/orderTool/components/HelpTip";
import {
  getBankRegles,
  createBankRegle,
  updateBankRegle,
  deleteBankRegle,
  reappliquerBankRegles,
} from "state/banque/actions";

const CIBLE_LABEL = { LIBELLE: "Libellé", EMETTEUR: "Émetteur", TOUS: "Libellé + Émetteur" };

const formVide = () => ({
  id: null, libelle: "", motif: "", isRegex: true, champCible: "TOUS", categorie: "", priorite: 100, active: true,
});

/**
 * Admin des règles de catégorisation bancaire (façon Agicap) : la première
 * règle (priorité croissante) dont le motif matche attribue la catégorie
 * proposée. Édition inline + réapplication aux flux en réception.
 */
const ReglesCategorisation = () => {
  const dispatch = useDispatch();
  const [regles, setRegles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(formVide());

  const charger = useCallback(async () => {
    setLoading(true);
    const resp = await dispatch(getBankRegles());
    setLoading(false);
    const body = resp?.result || resp || {};
    if (resp?.error || body.success === false) {
      toast.error(body.message || "Chargement des règles impossible");
      return;
    }
    setRegles(body.regles || []);
    setCategories(body.categories || []);
  }, [dispatch]);

  useEffect(() => { charger(); }, [charger]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const editer = (r) => setForm({
    id: r.id, libelle: r.libelle || "", motif: r.motif, isRegex: r.isRegex,
    champCible: r.champCible, categorie: r.categorie, priorite: r.priorite, active: r.active,
  });

  const annulerEdition = () => setForm(formVide());

  const enregistrer = async () => {
    if (!form.motif.trim()) { toast.error("Le motif est obligatoire"); return; }
    if (!form.categorie) { toast.error("Choisissez une catégorie"); return; }
    const payload = {
      libelle: form.libelle || null, motif: form.motif, isRegex: !!form.isRegex,
      champCible: form.champCible, categorie: form.categorie, priorite: Number(form.priorite) || 100, active: !!form.active,
    };
    setBusy(true);
    const resp = await dispatch(form.id ? updateBankRegle(form.id, payload) : createBankRegle(payload));
    setBusy(false);
    const body = resp?.result || resp || {};
    if (resp?.error || body.success === false) {
      toast.error(body.message || "Enregistrement impossible");
      return;
    }
    toast.success(form.id ? "Règle mise à jour" : "Règle créée");
    setForm(formVide());
    charger();
  };

  const supprimer = async (r) => {
    if (!window.confirm(`Supprimer la règle « ${r.libelle || r.motif} » ?`)) return;
    setBusy(true);
    const resp = await dispatch(deleteBankRegle(r.id));
    setBusy(false);
    const body = resp?.result || resp || {};
    if (resp?.error || body.success === false) {
      toast.error(body.message || "Suppression impossible");
      return;
    }
    toast.success("Règle supprimée");
    if (form.id === r.id) setForm(formVide());
    charger();
  };

  const reappliquer = async () => {
    if (!window.confirm("Ré-appliquer les règles aux flux encore en boîte de réception ? (les flux validés/traités ne sont pas touchés)")) return;
    setBusy(true);
    const resp = await dispatch(reappliquerBankRegles());
    setBusy(false);
    const body = resp?.result || resp || {};
    if (resp?.error || body.success === false) {
      toast.error(body.message || "Réapplication impossible");
      return;
    }
    toast.success(body.message || "Règles ré-appliquées");
  };

  return (
    <div style={{ position: "relative" }}>
      <LoadingOverlay show={loading || busy} message="Chargement…" minHeight={150} />

      {/* Formulaire création / édition */}
      <div className="border rounded p-2 mb-3 bg-light">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{form.id ? `Modifier la règle #${form.id}` : "Nouvelle règle"}</strong>
          <HelpTip title="Règles de catégorisation">
            <p className="mb-1">Les règles sont évaluées par <strong>priorité croissante</strong> : la première qui matche attribue la catégorie.</p>
            <p className="mb-1">Le <strong>motif</strong> est testé en « contient » ou en <strong>expression régulière</strong> (case insensible) sur le champ cible.</p>
            <p className="mb-0">La catégorie reste <strong>une proposition à valider</strong> dans la boîte de réception.</p>
          </HelpTip>
        </div>
        <Form>
          <div className="d-flex flex-wrap" style={{ gap: 8 }}>
            <Form.Group className="mb-1" style={{ flex: "1 1 200px" }}>
              <Form.Label className="mb-0" style={{ fontSize: "0.8rem" }}>Libellé</Form.Label>
              <Form.Control size="sm" value={form.libelle} placeholder="Ex : Titres-restaurant" onChange={(e) => set("libelle", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-1" style={{ flex: "2 1 320px" }}>
              <Form.Label className="mb-0" style={{ fontSize: "0.8rem" }}>Motif</Form.Label>
              <Form.Control size="sm" value={form.motif} placeholder="Ex : PLUXEE|EDENRED|SWILE" onChange={(e) => set("motif", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-1" style={{ flex: "1 1 160px" }}>
              <Form.Label className="mb-0" style={{ fontSize: "0.8rem" }}>Catégorie</Form.Label>
              <Form.Control as="select" size="sm" value={form.categorie} onChange={(e) => set("categorie", e.target.value)}>
                <option value="">— Choisir —</option>
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </Form.Control>
            </Form.Group>
            <Form.Group className="mb-1" style={{ flex: "1 1 140px" }}>
              <Form.Label className="mb-0" style={{ fontSize: "0.8rem" }}>Champ cible</Form.Label>
              <Form.Control as="select" size="sm" value={form.champCible} onChange={(e) => set("champCible", e.target.value)}>
                {Object.keys(CIBLE_LABEL).map((k) => <option key={k} value={k}>{CIBLE_LABEL[k]}</option>)}
              </Form.Control>
            </Form.Group>
            <Form.Group className="mb-1" style={{ flex: "0 0 90px" }}>
              <Form.Label className="mb-0" style={{ fontSize: "0.8rem" }}>Priorité</Form.Label>
              <Form.Control type="number" size="sm" value={form.priorite} onChange={(e) => set("priorite", e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-1 d-flex flex-column justify-content-end" style={{ flex: "0 0 90px" }}>
              <Form.Check type="checkbox" label="Regex" checked={!!form.isRegex} onChange={(e) => set("isRegex", e.target.checked)} />
              <Form.Check type="checkbox" label="Active" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} />
            </Form.Group>
          </div>
          <div className="mt-1 d-flex" style={{ gap: 8 }}>
            <Button size="sm" variant="primary" onClick={enregistrer} disabled={busy}>
              {form.id ? "Enregistrer" : "Ajouter la règle"}
            </Button>
            {form.id && <Button size="sm" variant="outline-secondary" onClick={annulerEdition} disabled={busy}>Annuler</Button>}
            <div className="ml-auto">
              <Button size="sm" variant="outline-dark" onClick={reappliquer} disabled={busy}>↻ Ré-appliquer aux flux en réception</Button>
            </div>
          </div>
        </Form>
      </div>

      <Table size="sm" hover striped responsive>
        <thead>
          <tr>
            <th style={{ width: 70 }}>Priorité</th>
            <th>Libellé</th>
            <th>Motif</th>
            <th>Cible</th>
            <th>Catégorie</th>
            <th style={{ width: 70 }}>Regex</th>
            <th style={{ width: 70 }}>Active</th>
            <th style={{ width: 140 }}></th>
          </tr>
        </thead>
        <tbody>
          {regles.map((r) => (
            <tr key={r.id} style={{ opacity: r.active ? 1 : 0.5 }}>
              <td>{r.priorite}</td>
              <td>{r.libelle || <span className="text-muted">—</span>}</td>
              <td><code style={{ fontSize: "0.78rem" }}>{r.motif}</code></td>
              <td>{CIBLE_LABEL[r.champCible] || r.champCible}</td>
              <td><Badge variant="light" className="border">{r.categorieLabel}</Badge></td>
              <td>{r.isRegex ? "✓" : "—"}</td>
              <td>{r.active ? <Badge variant="success">oui</Badge> : <Badge variant="secondary">non</Badge>}</td>
              <td>
                <Button size="sm" variant="outline-primary" className="py-0 px-1 mr-1" onClick={() => editer(r)} disabled={busy}>Éditer</Button>
                <Button size="sm" variant="outline-danger" className="py-0 px-1" onClick={() => supprimer(r)} disabled={busy}>Suppr.</Button>
              </td>
            </tr>
          ))}
          {regles.length === 0 && !loading && (
            <tr><td colSpan={8} className="text-muted text-center py-3">Aucune règle. Ajoutez-en une ci-dessus.</td></tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default ReglesCategorisation;
